if (!customElements.get('volume-bundle-picker')) {
  customElements.define(
    'volume-bundle-picker',
    class VolumeBundlePicker extends HTMLElement {
      constructor() {
        super();

        this.variants = JSON.parse(
          this.querySelector('[data-volume-variants]')?.textContent || '[]'
        );
        this.currency = this.dataset.currency || 'EUR';
        const parsePrice = (value) => {
          const normalizedValue = String(value || '0')
            .trim()
            .replace(',', '.');

          const parsedValue = Number(normalizedValue);

          return Number.isFinite(parsedValue) ? parsedValue : 0;
        };

        this.prices = {
          1: parsePrice(this.getAttribute('data-price-1')),
          2: parsePrice(this.getAttribute('data-price-2')),
          3: parsePrice(this.getAttribute('data-price-3'))
        };
        this.quantity = Number(this.dataset.defaultQuantity || 1);
        this.submitButton = this.querySelector('[data-volume-submit]');
        this.errorElement = this.querySelector('[data-volume-error]');
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
      }

      connectedCallback() {
        this.addEventListener('change', this.onChange.bind(this));
        this.submitButton?.addEventListener('click', this.addToCart.bind(this));
        this.refresh();
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          style: 'currency',
          currency: this.currency
        }).format(cents / 100);
      }

      onChange(event) {
        const tierInput = event.target.closest('input[name^="lk-volume-quantity-"]');

        if (tierInput) {
          this.quantity = Number(tierInput.value);
        }

      const swatchInput = event.target.closest('.lk-volume-swatch input');

      if (swatchInput) {
        const swatches = swatchInput.closest(
          '.lk-volume-option__color-swatches, .lk-volume-option__swatches'
        );

        swatches?.querySelectorAll('.lk-volume-swatch').forEach((swatch) => {
          const input = swatch.querySelector('input[type="radio"]');

          swatch.classList.toggle(
            'is-selected',
            input === swatchInput && swatchInput.checked
          );
        });
      }

        const textChoiceInput = event.target.closest('.lk-volume-text-choice input');
        if (textChoiceInput) {
          const choices = textChoiceInput.closest('.lk-volume-option__text-choices');
          choices?.querySelectorAll('.lk-volume-text-choice').forEach((choice) => {
            choice.classList.toggle('is-selected', choice.contains(textChoiceInput));
          });
        }

        this.clearError();
        this.refresh();
      }

      refresh() {
        this.querySelectorAll('.lk-volume-tier').forEach((tier) => {
          tier.classList.toggle(
            'is-selected',
            Number(tier.querySelector('input')?.value) === this.quantity
          );
        });

        this.querySelectorAll('[data-volume-set]').forEach((set) => {
          const setNumber = Number(set.dataset.volumeSet);
          set.classList.toggle('is-hidden', setNumber > this.quantity);

          if (setNumber <= this.quantity) {
            this.updateSetAvailability(set);
          }
        });

        [2, 3].forEach((quantity) => {
          const saving = quantity * (this.prices[1] - this.prices[quantity]);
          const savingElement = this.querySelector(
            `[data-tier-saving="${quantity}"]`
          );
          if (savingElement) {
            savingElement.textContent =
              saving > 0 ? `${this.formatMoney(saving)} sparen` : '';
            savingElement.hidden = saving <= 0;
          }
        });

        const total = this.prices[this.quantity] * this.quantity;
        const totalElement = this.querySelector('[data-volume-total]');
        const captionElement = this.querySelector('[data-volume-caption]');

        if (totalElement) totalElement.textContent = this.formatMoney(total);
        if (captionElement) {
          captionElement.textContent = `${this.quantity} Set${
            this.quantity > 1 ? 's' : ''
          }, frei kombinierbar`;
        }

        this.updateSubmitState();
      }

      getSelectedOptions(set) {
        const values = [];

        set.querySelectorAll('.lk-volume-option').forEach((optionGroup) => {
          const optionIndex = Number(optionGroup.dataset.optionIndex);
          const checked = optionGroup.querySelector('input[data-volume-option]:checked');
          const select = optionGroup.querySelector('select[data-volume-option]');
          values[optionIndex] = checked?.value ?? select?.value ?? '';
        });

        return values;
      }

      getVariantForSet(set) {
        const selectedOptions = this.getSelectedOptions(set);
        return this.variants.find(
          (variant) =>
            variant.options.length === selectedOptions.length &&
            variant.options.every((value, index) => value === selectedOptions[index])
        );
      }

      updateSetAvailability(set) {
        const variant = this.getVariantForSet(set);
        const message = set.querySelector('[data-set-availability]');
        const status = set.querySelector('[data-set-status]');
        const available = Boolean(variant?.available);

        set.dataset.variantId = variant?.id || '';
        set.classList.toggle('is-unavailable', !available);

        if (message) {
          message.textContent = variant
            ? available
              ? ''
              : 'Diese Zusammenstellung ist derzeit nicht verfügbar.'
            : 'Für diese Auswahl wurde keine passende Variante gefunden.';
        }

        if (status) status.classList.toggle('is-incomplete', !available);
      }

      activeSets() {
        return Array.from(this.querySelectorAll('[data-volume-set]')).filter(
          (set) => Number(set.dataset.volumeSet) <= this.quantity
        );
      }

      updateSubmitState() {
        if (!this.submitButton) return;
        const allAvailable = this.activeSets().every(
          (set) => Boolean(this.getVariantForSet(set)?.available)
        );
        this.submitButton.disabled = !allAvailable;
      }

      createBundleId() {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return `lk-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      }

      async addToCart() {
        if (!this.submitButton || this.submitButton.disabled) return;

        const sets = this.activeSets();
        const variants = sets.map((set) => this.getVariantForSet(set));

        if (variants.some((variant) => !variant?.available)) {
          this.showError('Mindestens eine Zusammenstellung ist nicht verfügbar.');
          return;
        }

        const bundleId = this.createBundleId();
        const items = variants.map((variant, index) => ({
          id: variant.id,
          quantity: 1,
          properties: {
            _lk_bundle_id: bundleId,
            _lk_bundle_size: String(this.quantity),
            _lk_bundle_position: String(index + 1)
          }
        }));

        const body = { items };

        if (this.cart?.getSectionsToRender) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement?.(document.activeElement);
        }

        this.setLoading(true);
        this.clearError();

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(body)
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'Die Sets konnten nicht hinzugefügt werden.');
          }

          if (this.cart?.renderContents && result.sections) {
            this.cart.renderContents(result);
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
            return;
          }

          if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'volume-bundle-picker' });
          }
        } catch (error) {
          this.showError(error.message || 'Die Sets konnten nicht hinzugefügt werden.');
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        this.submitButton.toggleAttribute('aria-disabled', isLoading);
        this.submitButton.classList.toggle('loading', isLoading);
        const spinner = this.querySelector('[data-volume-spinner]');
        spinner?.classList.toggle('hidden', !isLoading);
      }

      showError(message) {
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
      }

      clearError() {
        if (!this.errorElement) return;
        this.errorElement.textContent = '';
        this.errorElement.hidden = true;
      }
    }
  );
}
